import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Home, MapPin, Search, Sparkles, Loader2, Check, ArrowLeft, Plus, Building } from 'lucide-react';
import { slugify } from '@/lib/slugify';

interface AreaSuggestion {
  name: string;
  townName: string;
  townState: string;
  description: string;
}

interface TownData {
  id: string;
  name: string;
  state: string | null;
}

export default function AreaSetup() {
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, loading: globalAdminLoading } = useGlobalAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const [towns, setTowns] = useState<TownData[]>([]);
  const [suggestions, setSuggestions] = useState<AreaSuggestion[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTown, setFilterTown] = useState<string>('all');
  const [customArea, setCustomArea] = useState('');
  const [customTownId, setCustomTownId] = useState('');

  // Get newly created town IDs from navigation state
  const newTownIds = (location.state as { newTownIds?: string[] })?.newTownIds;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }

    if (user && !globalAdminLoading && selectedSiteId) {
      fetchTownsAndSuggestions();
    }
  }, [user, authLoading, globalAdminLoading, selectedSiteId, navigate]);

  const fetchTownsAndSuggestions = async () => {
    if (!selectedSiteId) {
      setLoading(false);
      return;
    }

    try {
      // Get towns - either newly created ones or all towns for the site
      let query = supabase
        .from('towns')
        .select('id, name, state')
        .eq('site_id', selectedSiteId);

      if (newTownIds && newTownIds.length > 0) {
        query = query.in('id', newTownIds);
      }

      const { data: townData } = await query;

      if (townData && townData.length > 0) {
        setTowns(townData);
        // Auto-fetch suggestions for these towns
        await fetchSuggestions(townData);
      }
    } catch (error) {
      console.error('Error fetching towns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async (townList: TownData[]) => {
    setSuggestionsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('suggest-areas', {
        body: { 
          towns: townList.map(t => ({ name: t.name, state: t.state || 'MA' }))
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setSuggestions(data.areas || []);
    } catch (error: any) {
      console.error('Error fetching suggestions:', error);
      toast({
        title: 'Error fetching area suggestions',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const getAreaKey = (area: AreaSuggestion) => `${area.name}|${area.townName}`;

  const toggleArea = (areaKey: string) => {
    const newSelected = new Set(selectedAreas);
    if (newSelected.has(areaKey)) {
      newSelected.delete(areaKey);
    } else {
      newSelected.add(areaKey);
    }
    setSelectedAreas(newSelected);
  };

  const selectAll = () => {
    const allKeys = filteredSuggestions.map(a => getAreaKey(a));
    setSelectedAreas(new Set(allKeys));
  };

  const deselectAll = () => {
    setSelectedAreas(new Set());
  };

  const addCustomArea = () => {
    if (!customArea.trim() || !customTownId) {
      toast({
        title: 'Missing information',
        description: 'Please enter area name and select a town.',
        variant: 'destructive',
      });
      return;
    }

    const town = towns.find(t => t.id === customTownId);
    if (!town) return;

    const newArea: AreaSuggestion = {
      name: customArea.trim(),
      townName: town.name,
      townState: town.state || 'MA',
      description: 'Custom added area',
    };

    // Check if already exists
    const exists = suggestions.some(
      a => a.name.toLowerCase() === newArea.name.toLowerCase() && 
           a.townName.toLowerCase() === newArea.townName.toLowerCase()
    );

    if (!exists) {
      setSuggestions([newArea, ...suggestions]);
      setSelectedAreas(new Set([getAreaKey(newArea), ...selectedAreas]));
    }

    setCustomArea('');
    setCustomTownId('');
  };

  const saveAreas = async () => {
    if (selectedAreas.size === 0) {
      toast({
        title: 'No areas selected',
        description: 'Please select at least one area to add.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      // Build the insert data with proper town_id lookups
      const areasToInsert = Array.from(selectedAreas).map(key => {
        const [areaName, townName] = key.split('|');
        const town = towns.find(t => t.name.toLowerCase() === townName.toLowerCase());
        const suggestion = suggestions.find(s => s.name === areaName && s.townName === townName);
        
        return {
          site_id: selectedSiteId!,
          town_id: town?.id,
          name: areaName,
          slug: slugify(areaName),
          intro_summary: suggestion?.description || null,
          is_published: false,
        };
      }).filter(a => a.town_id); // Only include areas where we found the town

      if (areasToInsert.length === 0) {
        throw new Error('Could not match areas to towns');
      }

      const { data: insertedAreas, error } = await supabase.from('areas').insert(areasToInsert).select('id');

      if (error) {
        if (error.code === '23505') {
          throw new Error('Some areas already exist. Please refresh and try again.');
        }
        throw error;
      }

      const newAreaIds = insertedAreas?.map(a => a.id) || [];

      // Generate AI content for the new areas
      toast({
        title: 'Generating area content...',
        description: 'AI is writing detailed content for your neighborhoods. This may take a moment.',
      });

      try {
        const { data: contentResult, error: contentError } = await supabase.functions.invoke('generate-area-content', {
          body: { areaIds: newAreaIds, generateImages: true },
        });

        if (contentError) {
          console.error('Area content generation error:', contentError);
          toast({
            title: 'Content generation issue',
            description: 'Areas were saved but some content could not be generated. You can regenerate later.',
            variant: 'destructive',
          });
        } else {
          console.log('Area content generation result:', contentResult);
          toast({
            title: 'Areas created with AI content!',
            description: contentResult.message,
          });
        }
      } catch (contentErr: any) {
        console.error('Area content generation failed:', contentErr);
        toast({
          title: 'Areas saved',
          description: 'Areas were saved. AI content generation is being processed.',
        });
      }

      navigate('/admin');
    } catch (error: any) {
      console.error('Error saving areas:', error);
      toast({
        title: 'Error saving areas',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const filteredSuggestions = suggestions.filter(area => {
    const matchesSearch = 
      area.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      area.townName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTown = filterTown === 'all' || area.townName === filterTown;
    return matchesSearch && matchesTown;
  });

  // Group suggestions by town
  const groupedSuggestions = filteredSuggestions.reduce((acc, area) => {
    if (!acc[area.townName]) {
      acc[area.townName] = [];
    }
    acc[area.townName].push(area);
    return acc;
  }, {} as Record<string, AreaSuggestion[]>);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Building className="h-6 w-6 text-primary" />
              <span className="font-display text-xl">Area Setup</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              Skip for now
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-accent" />
            Select Neighborhoods & Areas
          </h1>
          <p className="text-muted-foreground mt-2">
            AI-suggested areas for your {towns.length} selected town{towns.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Add Custom Area */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Add Custom Area</CardTitle>
            <CardDescription>Don't see an area? Add it manually.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Area name"
                value={customArea}
                onChange={(e) => setCustomArea(e.target.value)}
                className="flex-1"
              />
              <select
                value={customTownId}
                onChange={(e) => setCustomTownId(e.target.value)}
                className="px-3 py-2 border border-input rounded-md bg-background text-sm"
              >
                <option value="">Select town...</option>
                {towns.map(town => (
                  <option key={town.id} value={town.id}>
                    {town.name}, {town.state}
                  </option>
                ))}
              </select>
              <Button onClick={addCustomArea} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search areas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={filterTown}
            onChange={(e) => setFilterTown(e.target.value)}
            className="px-3 py-2 border border-input rounded-md bg-background text-sm"
          >
            <option value="all">All Towns</option>
            {towns.map(town => (
              <option key={town.id} value={town.name}>
                {town.name}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll}>
              Select All
            </Button>
            <Button variant="outline" size="sm" onClick={deselectAll}>
              Deselect All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchSuggestions(towns)}
              disabled={suggestionsLoading}
            >
              {suggestionsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Area Suggestions */}
        {suggestionsLoading ? (
          <Card className="p-12">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-accent" />
              <p className="text-muted-foreground">AI is finding neighborhoods for your towns...</p>
            </div>
          </Card>
        ) : Object.keys(groupedSuggestions).length === 0 ? (
          <Card className="p-8">
            <div className="text-center text-muted-foreground">
              No areas found. Try refreshing or add custom areas.
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedSuggestions).map(([townName, areas]) => (
              <Card key={townName}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    {townName}
                  </CardTitle>
                  <CardDescription>{areas.length} area{areas.length !== 1 ? 's' : ''}</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {areas.map((area) => {
                      const areaKey = getAreaKey(area);
                      const isSelected = selectedAreas.has(areaKey);
                      
                      return (
                        <label
                          key={areaKey}
                          className={`flex items-start gap-4 p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                            isSelected ? 'bg-accent/10' : ''
                          }`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleArea(areaKey)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="font-medium">{area.name}</span>
                              {isSelected && (
                                <Check className="h-4 w-4 text-estate-success ml-auto shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {area.description}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Save Button */}
        <div className="mt-8 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {selectedAreas.size} area{selectedAreas.size !== 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Skip
            </Button>
            <Button
              variant="gold"
              size="lg"
              onClick={saveAreas}
              disabled={saving || selectedAreas.size === 0}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Add {selectedAreas.size} Area{selectedAreas.size !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}